// ./src/contexts/MarketingChannelsContext/MarketingChannelsContext.tsx
//
// Thin context wrapper around the useMarketingChannels hook. All derived data
// lives in the hook itself. See CouponTypesContext for the pattern. The same
// provider backs both the account Library page and the per-event "get URLs / QR"
// page; the per-event page passes an `eventId` (forwarded via ...options to the
// hook) as an authorization context so crew with event.operate can manage the
// event account's channels. Channels themselves have no event FK.

import React, { createContext, useContext } from "react";

import {
  useMarketingChannels,
  UseMarketingChannelsOptions,
  UseMarketingChannelsReturn,
} from "@/hooks/useMarketingChannels";

const MarketingChannelsContext = createContext<UseMarketingChannelsReturn | null>(null);

export interface MarketingChannelsProviderProps extends UseMarketingChannelsOptions {
  children: React.ReactNode;
}

export const MarketingChannelsProvider: React.FC<MarketingChannelsProviderProps> = ({ children, ...options }) => {
  const value = useMarketingChannels(options);

  return <MarketingChannelsContext.Provider value={value}>{children}</MarketingChannelsContext.Provider>;
};

export const useMarketingChannelsContext = (): UseMarketingChannelsReturn => {
  const context = useContext(MarketingChannelsContext);

  if (!context) {
    throw new Error(
      "useMarketingChannelsContext must be used within a <MarketingChannelsProvider>. " +
        "Wrap your component tree with <MarketingChannelsProvider> to fix this."
    );
  }

  return context;
};

export const useOptionalMarketingChannelsContext = (): UseMarketingChannelsReturn | null => {
  return useContext(MarketingChannelsContext);
};
